import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { proxyAdminApi, type ProxyInfo, type ProxyOperation, type ProxyOperationsFilters, type ProxyOutage, type LatestVersion } from "@services/api/proxyAdmin";
import { showNotification } from "context/notification.service";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import BaseModal from "@components/modals/BaseModal";

function getStatusInfo(proxy: ProxyInfo): { label: string; color: string } {
  if (!proxy.proxy_url) {
    return { label: "Non configuré", color: "bg-neutral-300" };
  }
  if (!proxy.proxy_last_seen) {
    return { label: "Jamais vu", color: "bg-neutral-400" };
  }
  const lastSeen = new Date(proxy.proxy_last_seen);
  const diffMin = (Date.now() - lastSeen.getTime()) / 60000;
  if (diffMin < 7) {
    return { label: "En ligne", color: "bg-green-500" };
  }
  if (diffMin < 30) {
    return { label: "Inactif", color: "bg-yellow-500" };
  }
  return { label: "Hors ligne", color: "bg-red-500" };
}

function formatLastSeen(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  const now = Date.now();
  const diffMin = Math.floor((now - d.getTime()) / 60000);
  if (diffMin < 1) return "< 1 min";
  if (diffMin < 60) return `${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  return `${diffD}j`;
}

function formatUptime(seconds: number | undefined): string {
  if (!seconds) return "-";
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}j ${hours}h`;
  const mins = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

function isOutdated(current: string, latest: string | null): boolean {
  if (!latest || !current) return false;
  return current !== latest;
}

function buildSparklineData(operations: ProxyInfo["proxy_recent_operations"]): number[] {
  const now = new Date();
  const counts = Array(30).fill(0);
  for (const op of operations || []) {
    if (!op.timestamp) continue;
    const dayDiff = Math.floor((now.getTime() - new Date(op.timestamp).getTime()) / 86400000);
    if (dayDiff >= 0 && dayDiff < 30) {
      counts[29 - dayDiff] += 1;
    }
  }
  return counts;
}

function Sparkline({ data, color = "#6366F1" }: { data: number[]; color?: string }) {
  const max = Math.max(...data, 1);
  const w = 80;
  const h = 24;
  const points = data.map((v, i) => ({
    x: data.length > 1 ? (i / (data.length - 1)) * w : w / 2,
    y: h - (v / max) * (h - 4) - 2,
  }));
  const pathD = points.reduce((acc, p, i) => {
    if (i === 0) return `M ${p.x} ${p.y}`;
    const prev = points[i - 1];
    const cpx = (prev.x + p.x) / 2;
    return acc + ` C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
  }, "");
  const fillD = `${pathD} L ${points[points.length - 1].x} ${h} L ${points[0].x} ${h} Z`;

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <path d={fillD} fill={color} fillOpacity={0.1} />
      <path d={pathD} fill="none" stroke={color} strokeWidth={1.5} />
    </svg>
  );
}

function formatOutageDuration(min: number): string {
  if (!min || min < 1) return "< 1 min";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}

function formatOutageDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Ongoing outages have no end_at yet — label them rather than rendering "Invalid Date".
function formatOutageEnd(iso: string | null): string {
  return iso ? formatOutageDate(iso) : "en cours";
}

function buildOutageSegments(
  outages: ProxyOutage[] | undefined,
  windowDays: number,
  width: number,
): { x: number; w: number; outage: ProxyOutage }[] {
  const now = Date.now();
  const windowMs = windowDays * 86400 * 1000;
  const windowStart = now - windowMs;
  return (outages || [])
    .map((o) => {
      const startMs = new Date(o.start_at).getTime();
      if (isNaN(startMs)) return null;
      // Ongoing outage (end_at null) → render it red right up to "now".
      const endRaw = o.end_at ? new Date(o.end_at).getTime() : now;
      const endMs = isNaN(endRaw) ? now : endRaw;
      const clampedStart = Math.max(startMs, windowStart);
      const clampedEnd = Math.min(endMs, now);
      if (clampedEnd <= clampedStart) return null;
      const x = ((clampedStart - windowStart) / windowMs) * width;
      const w = Math.max(((clampedEnd - clampedStart) / windowMs) * width, 1.5);
      return { x, w, outage: o };
    })
    .filter((s): s is { x: number; w: number; outage: ProxyOutage } => s !== null);
}

function computeUptimePct(
  outages: ProxyOutage[] | undefined,
  windowDays: number,
): number {
  if (!outages || outages.length === 0) return 100;
  const totalMin = outages.reduce((sum, o) => sum + (o.duration_min || 0), 0);
  const windowMin = windowDays * 24 * 60;
  return Math.max(0, Math.min(100, (1 - totalMin / windowMin) * 100));
}

function UptimeBar({
  outages,
  width = 110,
  height = 12,
  windowDays = 30,
}: {
  outages: ProxyOutage[] | undefined;
  width?: number;
  height?: number;
  windowDays?: number;
}) {
  const segments = useMemo(
    () => buildOutageSegments(outages, windowDays, width),
    [outages, windowDays, width],
  );
  const uptimePct = useMemo(() => computeUptimePct(outages, windowDays), [outages, windowDays]);

  const pctLabel = uptimePct >= 99.995 ? "100%" : `${uptimePct.toFixed(2)}%`;
  const pctColor =
    uptimePct >= 99.5 ? "text-green-600" : uptimePct >= 98 ? "text-yellow-600" : "text-red-600";
  const count = outages?.length ?? 0;

  return (
    <div className="flex items-center gap-2">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <rect x={0} y={0} width={width} height={height} rx={2} fill="#22C55E" fillOpacity={0.25} />
        <rect x={0} y={0} width={width} height={height} rx={2} fill="none" stroke="#22C55E" strokeOpacity={0.5} strokeWidth={0.5} />
        {segments.map((s, i) => (
          <rect key={i} x={s.x} y={0} width={s.w} height={height} fill="#EF4444">
            <title>
              {`Coupure ${formatOutageDuration(s.outage.duration_min)} — ${formatOutageDate(s.outage.start_at)} → ${formatOutageEnd(s.outage.end_at)}`}
            </title>
          </rect>
        ))}
      </svg>
      <span className={`text-h4 font-medium ${pctColor}`} title={`${count} coupure(s) sur ${windowDays}j`}>
        {pctLabel}
      </span>
    </div>
  );
}

function UptimeDetail({
  outages,
  windowDays = 30,
}: {
  outages: ProxyOutage[] | undefined;
  windowDays?: number;
}) {
  const width = 700;
  const height = 22;
  const segments = useMemo(
    () => buildOutageSegments(outages, windowDays, width),
    [outages, windowDays, width],
  );
  const uptimePct = useMemo(() => computeUptimePct(outages, windowDays), [outages, windowDays]);
  const count = outages?.length ?? 0;
  const labels = [0, 7, 14, 21, 30].map((daysAgo) => ({
    x: ((windowDays - daysAgo) / windowDays) * width,
    label: daysAgo === 0 ? "maint." : `-${daysAgo}j`,
  }));

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div className="font-medium text-h3">Disponibilité — {windowDays} jours</div>
        <div className="text-h4 text-neutral-500">
          {count === 0
            ? "Aucune coupure enregistrée"
            : `${count} coupure${count > 1 ? "s" : ""} — ${uptimePct.toFixed(2)}% uptime`}
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height + 18}`} width="100%" preserveAspectRatio="none">
        <rect x={0} y={0} width={width} height={height} rx={3} fill="#22C55E" fillOpacity={0.25} />
        <rect x={0} y={0} width={width} height={height} rx={3} fill="none" stroke="#22C55E" strokeOpacity={0.5} strokeWidth={1} />
        {segments.map((s, i) => (
          <rect key={i} x={s.x} y={0} width={s.w} height={height} fill="#EF4444">
            <title>
              {`Coupure ${formatOutageDuration(s.outage.duration_min)} — ${formatOutageDate(s.outage.start_at)} → ${formatOutageEnd(s.outage.end_at)}`}
            </title>
          </rect>
        ))}
        {labels.map((l) => (
          <g key={l.label}>
            <line x1={l.x} y1={height} x2={l.x} y2={height + 4} stroke="#a3a3a3" strokeWidth={0.5} />
            <text x={l.x} y={height + 14} textAnchor="middle" fontSize={10} fill="#a3a3a3">{l.label}</text>
          </g>
        ))}
      </svg>
      {count > 0 && (
        <div className="flex flex-col gap-1 max-h-48 overflow-y-auto mt-1">
          <div className="grid grid-cols-3 gap-2 text-h5 text-neutral-500 px-2">
            <span>Début</span>
            <span>Fin</span>
            <span>Durée</span>
          </div>
          {outages!.map((o, i) => (
            <div key={i} className="grid grid-cols-3 gap-2 text-h4 px-2 py-1 bg-red-50 rounded">
              <span className="font-mono">{formatOutageDate(o.start_at)}</span>
              <span className="font-mono">{formatOutageEnd(o.end_at)}</span>
              <span className="text-red-700 font-medium">{formatOutageDuration(o.duration_min)}{o.ongoing ? " (en cours)" : ""}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const PROXY_COLORS = ["#6366F1", "#F59E0B", "#10B981", "#F43F5E", "#8B5CF6", "#0EA5E9", "#EC4899", "#14B8A6"];

function buildDayLabels(): { key: string; label: string; tooltipLabel: string }[] {
  const now = new Date();
  const days: { key: string; label: string; tooltipLabel: string }[] = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const label = d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }).replace(".", "");
    days.push({ key, label, tooltipLabel: label });
  }
  return days;
}

function OperationsComparisonChart({ proxies }: { proxies: ProxyInfo[] }) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const days = useMemo(() => buildDayLabels(), []);
  const series = useMemo(() => {
    return proxies
      .filter((p) => p.proxy_url)
      .map((p, i) => {
        const data = buildSparklineData(p.proxy_recent_operations);
        return { name: p.org_name || p.org_id, color: PROXY_COLORS[i % PROXY_COLORS.length], data };
      });
  }, [proxies]);

  const max = useMemo(() => Math.max(...series.flatMap((s) => s.data), 1), [series]);
  const total = useMemo(() => series.reduce((sum, s) => sum + s.data.reduce((a, b) => a + b, 0), 0), [series]);

  const chartW = 700;
  const chartH = 140;

  const allPoints = useMemo(
    () =>
      series.map((s) =>
        s.data.map((v, i) => ({
          x: s.data.length > 1 ? (i / (s.data.length - 1)) * chartW : chartW / 2,
          y: chartH - (v / max) * (chartH - 16) - 4,
        }))
      ),
    [series, max, chartW, chartH]
  );

  const paths = useMemo(
    () =>
      allPoints.map((points) =>
        points.reduce((acc, p, i) => {
          if (i === 0) return `M ${p.x} ${p.y}`;
          const prev = points[i - 1];
          const cpx = (prev.x + p.x) / 2;
          return acc + ` C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
        }, "")
      ),
    [allPoints]
  );

  const labelIndices = useMemo(() => {
    const step = Math.ceil(days.length / 7);
    const indices = [0];
    for (let i = step; i < days.length - 1; i += step) indices.push(i);
    indices.push(days.length - 1);
    return indices;
  }, [days]);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      const svg = svgRef.current;
      if (!svg || days.length === 0) return;
      const rect = svg.getBoundingClientRect();
      const svgX = ((e.clientX - rect.left) / rect.width) * (chartW + 50);
      const stepW = chartW / (days.length - 1);
      const idx = Math.round(Math.min(Math.max(svgX / stepW, 0), days.length - 1));
      setHoveredIdx(idx);
    },
    [days, chartW]
  );

  if (total === 0) return null;

  const tooltipPctX = hoveredIdx !== null && allPoints[0]
    ? (allPoints[0][hoveredIdx].x / (chartW + 40)) * 100
    : 0;

  return (
    <div className="bg-white border border-neutral-200 rounded-xl p-4 flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-h2 font-semibold text-neutral-700">Opérations — 30 jours</span>
          <span className="ml-2 text-h3 text-neutral-400">{total} total</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {series.map((s) => (
            <div key={s.name} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ background: s.color }} />
              <span className="text-h4 text-neutral-600">{s.name}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="relative">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${chartW + 40} ${chartH + 24}`}
          width="100%"
          preserveAspectRatio="xMidYMid meet"
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoveredIdx(null)}
          style={{ cursor: "crosshair", display: "block" }}
        >
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((frac) => {
            const y = chartH - frac * (chartH - 16) - 4;
            const val = Math.round(max * frac);
            return (
              <g key={frac}>
                <line x1={0} y1={y} x2={chartW} y2={y} stroke="#f0f0f0" strokeWidth={1} />
                <text x={chartW + 6} y={y + 4} fontSize={10} fill="#a3a3a3">{val}</text>
              </g>
            );
          })}
          {/* Lines */}
          {paths.map((d, i) => (
            <path key={i} d={d} fill="none" stroke={series[i].color} strokeWidth={2} strokeOpacity={0.8} />
          ))}
          {/* Hover line + dots */}
          {hoveredIdx !== null && allPoints[0] && allPoints[0][hoveredIdx] && (
            <>
              <line
                x1={allPoints[0][hoveredIdx].x} y1={0}
                x2={allPoints[0][hoveredIdx].x} y2={chartH}
                stroke="#d1d5db" strokeWidth={1} strokeDasharray="4 2"
              />
              {allPoints.map((points, i) => (
                <circle
                  key={i}
                  cx={points[hoveredIdx].x}
                  cy={points[hoveredIdx].y}
                  r={4}
                  fill={series[i].color}
                  stroke="white"
                  strokeWidth={1.5}
                />
              ))}
            </>
          )}
          {/* X labels */}
          {labelIndices.map((i) => (
            <text key={i} x={(i / (days.length - 1)) * chartW} y={chartH + 16} textAnchor="middle" fontSize={10} fill="#a3a3a3">
              {days[i].label}
            </text>
          ))}
        </svg>
        {/* HTML tooltip */}
        {hoveredIdx !== null && allPoints[0] && allPoints[0][hoveredIdx] && (
          <div
            className="absolute pointer-events-none"
            style={{
              left: `${tooltipPctX}%`,
              top: 0,
              transform: `translateX(${tooltipPctX > 75 ? "-100%" : tooltipPctX < 25 ? "0%" : "-50%"})`,
            }}
          >
            <div className="bg-neutral-900 text-white rounded-lg px-3 py-2 shadow-lg" style={{ minWidth: 120 }}>
              <div className="text-h4 text-neutral-400 mb-1">{days[hoveredIdx].tooltipLabel}</div>
              {series.map((s, i) => (
                <div key={i} className="flex items-center justify-between gap-4 text-h4">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full" style={{ background: s.color }} />
                    <span className="text-neutral-300">{s.name}</span>
                  </div>
                  <span className="font-semibold">{s.data[hoveredIdx]}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

type ExpandedTab = "info" | "logs" | "operations";

export function ProxyDashboard() {
  const [proxies, setProxies] = useState<ProxyInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrg, setExpandedOrg] = useState<string | null>(null);
  const [expandedTab, setExpandedTab] = useState<ExpandedTab>("info");
  const [sendingCommand, setSendingCommand] = useState<string | null>(null);
  const [latestVersion, setLatestVersion] = useState<LatestVersion | null>(null);

  const fetchProxies = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [data, version] = await Promise.all([
        proxyAdminApi.list(),
        proxyAdminApi.getLatestVersion(),
      ]);
      setProxies(data);
      setLatestVersion(version);
    } catch (e) {
      if (!silent) {
        showNotification({ message: "Erreur lors du chargement des proxys", type: "error" });
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchProxies();
    const interval = setInterval(() => {
      fetchProxies(true);
    }, 30_000);
    return () => clearInterval(interval);
  }, []);

  const handleCommand = async (orgId: string, action: string) => {
    try {
      setSendingCommand(`${orgId}-${action}`);
      await proxyAdminApi.sendCommand(orgId, action);
      showNotification({ message: `Commande "${action}" envoyée`, type: "success" });
      fetchProxies();
    } catch (e) {
      showNotification({ message: "Erreur lors de l'envoi de la commande", type: "error" });
    } finally {
      setSendingCommand(null);
    }
  };

  const onlineCount = proxies.filter((p) => {
    if (!p.proxy_last_seen) return false;
    return (Date.now() - new Date(p.proxy_last_seen).getTime()) / 60000 < 7;
  }).length;
  const totalWithProxy = proxies.filter((p) => p.proxy_url).length;


  return (
    <div className="flex flex-col p-8 gap-6 max-w-[1200px]">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Tableau de bord Proxys</h1>
          <p className="text-neutral-500 text-h3 mt-1">
            Surveillance de tous les proxys ServiceBox actifs
            {latestVersion?.version && (
              <span className="ml-3 bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-h4">
                Dernière version : v{latestVersion.version}
              </span>
            )}
          </p>
        </div>
        <SecondaryButton
          label={loading ? "Chargement..." : "Rafraîchir"}
          onClick={fetchProxies}
          disabled={loading}
        />
      </div>

      {/* Summary cards */}
      <div className="flex gap-4">
        <div className="flex-1 bg-white border border-neutral-200 rounded-xl p-4">
          <div className="text-neutral-500 text-h4">Total proxys</div>
          <div className="text-2xl font-semibold mt-1">{totalWithProxy}</div>
        </div>
        <div className="flex-1 bg-white border border-neutral-200 rounded-xl p-4">
          <div className="text-neutral-500 text-h4">En ligne</div>
          <div className="text-2xl font-semibold mt-1 text-green-600">{onlineCount}</div>
        </div>
        <div className="flex-1 bg-white border border-neutral-200 rounded-xl p-4">
          <div className="text-neutral-500 text-h4">Hors ligne</div>
          <div className="text-2xl font-semibold mt-1 text-red-600">
            {totalWithProxy - onlineCount}
          </div>
        </div>
        <div className="flex-1 bg-white border border-neutral-200 rounded-xl p-4">
          <div className="text-neutral-500 text-h4">Organisations</div>
          <div className="text-2xl font-semibold mt-1">{proxies.length}</div>
        </div>
      </div>

      {/* Operations comparison chart */}
      <OperationsComparisonChart proxies={proxies} />

      {/* Proxy table */}
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <table className="w-full text-h3">
          <thead>
            <tr className="bg-neutral-50 text-neutral-500 text-left border-b border-neutral-200">
              <th className="px-4 py-3 font-medium">Statut</th>
              <th className="px-4 py-3 font-medium">Organisation</th>
              <th className="px-4 py-3 font-medium">Version</th>
              <th className="px-4 py-3 font-medium">Machine</th>
              <th className="px-4 py-3 font-medium">Dernier signal</th>
              <th className="px-4 py-3 font-medium">Disponibilité 30j</th>
              <th className="px-4 py-3 font-medium">Opérations</th>
              <th className="px-4 py-3 font-medium">30 jours</th>
              <th className="px-4 py-3 font-medium">Taux succès</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-neutral-400">
                  Chargement...
                </td>
              </tr>
            )}
            {!loading && proxies.length === 0 && (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-neutral-400">
                  Aucun proxy trouvé
                </td>
              </tr>
            )}
            {!loading &&
              proxies.map((proxy) => {
                const status = getStatusInfo(proxy);
                const stats = proxy.proxy_stats;
                const successRate =
                  stats && stats.total > 0
                    ? Math.round((stats.successes / stats.total) * 100)
                    : null;
                const isExpanded = expandedOrg === proxy.org_id;
                const outdated = isOutdated(proxy.proxy_version, latestVersion?.version ?? null);

                return (
                  <ProxyRow
                    key={proxy.org_id}
                    proxy={proxy}
                    status={status}
                    stats={stats}
                    successRate={successRate}
                    outdated={outdated}
                    isExpanded={isExpanded}
                    expandedTab={expandedTab}
                    sendingCommand={sendingCommand}
                    onToggleExpand={() => {
                      if (isExpanded) {
                        setExpandedOrg(null);
                      } else {
                        setExpandedOrg(proxy.org_id);
                        setExpandedTab("info");
                      }
                    }}
                    onTabChange={setExpandedTab}
                    onCommand={handleCommand}
                    latestVersion={latestVersion?.version ?? null}
                  />
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProxyRow({
  proxy,
  status,
  stats,
  successRate,
  outdated,
  isExpanded,
  expandedTab,
  sendingCommand,
  onToggleExpand,
  onTabChange,
  onCommand,
  latestVersion,
}: {
  proxy: ProxyInfo;
  status: { label: string; color: string };
  stats: ProxyInfo["proxy_stats"];
  successRate: number | null;
  outdated: boolean;
  isExpanded: boolean;
  expandedTab: ExpandedTab;
  sendingCommand: string | null;
  onToggleExpand: () => void;
  onTabChange: (tab: ExpandedTab) => void;
  onCommand: (orgId: string, action: string) => void;
  latestVersion: string | null;
}) {
  const sysInfo = proxy.proxy_system_info;

  return (
    <>
      <tr
        className="border-t border-neutral-100 hover:bg-neutral-50 cursor-pointer"
        onClick={onToggleExpand}
      >
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            <div className={`w-2.5 h-2.5 rounded-full ${status.color}`} />
            <span className="text-h4">{status.label}</span>
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="font-medium">{proxy.org_name || proxy.org_id}</div>
          <div className="text-h4 text-neutral-400">{proxy.servicebox_username}</div>
        </td>
        <td className="px-4 py-3">
          {proxy.proxy_version ? (
            <div className="flex items-center gap-1.5">
              <span
                className={`px-2 py-0.5 rounded text-h4 ${
                  outdated
                    ? "bg-orange-50 text-orange-700"
                    : "bg-blue-50 text-blue-700"
                }`}
              >
                v{proxy.proxy_version}
              </span>
              {outdated && (
                <span className="text-h4 text-orange-500" title={`Dernière : v${latestVersion}`}>
                  (obsolète)
                </span>
              )}
            </div>
          ) : (
            <span className="text-neutral-400">-</span>
          )}
        </td>
        <td className="px-4 py-3">
          {sysInfo ? (
            <div>
              <div className="font-mono text-h4">{sysInfo.hostname}</div>
              <div className="text-h4 text-neutral-400">
                Uptime : {formatUptime(sysInfo.uptime_seconds)}
              </div>
            </div>
          ) : (
            <span className="text-neutral-400">-</span>
          )}
        </td>
        <td className="px-4 py-3">{formatLastSeen(proxy.proxy_last_seen)}</td>
        <td className="px-4 py-3">
          <UptimeBar outages={proxy.proxy_outages_30d} />
        </td>
        <td className="px-4 py-3">{stats?.total ?? "-"}</td>
        <td className="px-4 py-3">
          <Sparkline data={buildSparklineData(proxy.proxy_recent_operations)} />
        </td>
        <td className="px-4 py-3">
          {successRate !== null ? (
            <span
              className={
                successRate >= 95
                  ? "text-green-600"
                  : successRate >= 80
                    ? "text-yellow-600"
                    : "text-red-600"
              }
            >
              {successRate}%
            </span>
          ) : (
            "-"
          )}
        </td>
        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-2">
            <button
              className="text-h4 text-blue-600 hover:text-blue-800 disabled:opacity-50"
              disabled={sendingCommand === `${proxy.org_id}-force-update`}
              onClick={() => onCommand(proxy.org_id, "force-update")}
            >
              Mettre à jour
            </button>
          </div>
        </td>
      </tr>

      {isExpanded && (
        <tr>
          <td colSpan={10} className="px-4 py-4 bg-neutral-50 border-t border-neutral-100">
            <ExpandedDetails
              proxy={proxy}
              stats={stats}
              sendingCommand={sendingCommand}
              onCommand={onCommand}
              activeTab={expandedTab}
              onTabChange={onTabChange}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function TabButton({
  label,
  active,
  onClick,
  count,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  count?: number;
}) {
  return (
    <button
      className={`px-3 py-1.5 rounded-lg text-h3 font-medium cursor-pointer ${
        active
          ? "bg-white text-neutral-800 shadow-sm border border-neutral-200"
          : "text-neutral-500 hover:text-neutral-700"
      }`}
      onClick={onClick}
    >
      {label}
      {count !== undefined && count > 0 && (
        <span className="ml-1.5 bg-neutral-200 text-neutral-600 px-1.5 py-0.5 rounded text-h4">
          {count}
        </span>
      )}
    </button>
  );
}

function ExpandedDetails({
  proxy,
  stats,
  sendingCommand,
  onCommand,
  activeTab,
  onTabChange,
}: {
  proxy: ProxyInfo;
  stats: ProxyInfo["proxy_stats"];
  sendingCommand: string | null;
  onCommand: (orgId: string, action: string) => void;
  activeTab: ExpandedTab;
  onTabChange: (tab: ExpandedTab) => void;
}) {
  const sysInfo = proxy.proxy_system_info;
  const errorCount = proxy.proxy_recent_errors?.length || 0;

  return (
    <div className="flex flex-col gap-4">
      {/* Info row */}
      <div className="flex gap-6 text-h4">
        <div>
          <span className="text-neutral-500">URL : </span>
          <span className="font-mono">{proxy.proxy_url || "-"}</span>
        </div>
        {sysInfo?.platform && (
          <div>
            <span className="text-neutral-500">OS : </span>
            <span>{sysInfo.platform}</span>
          </div>
        )}
        <div>
          <span className="text-neutral-500">ServiceBox : </span>
          <span>{proxy.servicebox_enabled ? "Activé" : "Désactivé"}</span>
        </div>
        {proxy.pending_commands.length > 0 && (
          <div>
            <span className="text-neutral-500">Commandes en attente : </span>
            <span className="text-yellow-600">
              {proxy.pending_commands.map((c) => c.action).join(", ")}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          label="Forcer la mise à jour"
          onClick={() => onCommand(proxy.org_id, "force-update")}
          loading={sendingCommand === `${proxy.org_id}-force-update`}
        />
        <SecondaryButton
          label="Reset session"
          onClick={() => onCommand(proxy.org_id, "reset-session")}
          disabled={sendingCommand === `${proxy.org_id}-reset-session`}
        />
      </div>

      {/* Uptime timeline + outage history */}
      <div className="bg-white border border-neutral-200 rounded-lg p-3">
        <UptimeDetail outages={proxy.proxy_outages_30d} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-neutral-100 rounded-lg p-1 w-fit">
        <TabButton label="Statistiques" active={activeTab === "info"} onClick={() => onTabChange("info")} count={errorCount} />
        <TabButton label="Logs" active={activeTab === "logs"} onClick={() => onTabChange("logs")} count={proxy.proxy_recent_logs?.length} />
        <TabButton label="Opérations" active={activeTab === "operations"} onClick={() => onTabChange("operations")} count={proxy.proxy_recent_operations?.length} />
      </div>

      {/* Tab content */}
      {activeTab === "info" && <InfoTab stats={stats} errors={proxy.proxy_recent_errors} />}
      {activeTab === "logs" && <LogsTab logs={proxy.proxy_recent_logs} />}
      {activeTab === "operations" && <OperationsTab orgId={proxy.org_id} operations={proxy.proxy_recent_operations} />}
    </div>
  );
}

function InfoTab({
  stats,
  errors,
}: {
  stats: ProxyInfo["proxy_stats"];
  errors: ProxyInfo["proxy_recent_errors"];
}) {
  return (
    <div className="flex flex-col gap-4">
      {stats && stats.by_operation && stats.by_operation.length > 0 && (
        <div>
          <div className="font-medium text-h3 mb-2">Détail par opération</div>
          <table className="w-full text-h4">
            <thead>
              <tr className="text-neutral-500 text-left">
                <th className="pr-4 py-1 font-medium">Opération</th>
                <th className="pr-4 py-1 font-medium">Total</th>
                <th className="pr-4 py-1 font-medium">Succès</th>
                <th className="pr-4 py-1 font-medium">Échecs</th>
                <th className="pr-4 py-1 font-medium">Taux</th>
                <th className="pr-4 py-1 font-medium">Durée moy.</th>
              </tr>
            </thead>
            <tbody>
              {stats.by_operation.map((op) => (
                <tr key={op.operation} className="border-t border-neutral-200">
                  <td className="pr-4 py-1 font-mono">{op.operation}</td>
                  <td className="pr-4 py-1">{op.total}</td>
                  <td className="pr-4 py-1 text-green-600">{op.successes}</td>
                  <td className="pr-4 py-1 text-red-600">{op.failures}</td>
                  <td className="pr-4 py-1">
                    <span
                      className={
                        op.success_rate >= 95
                          ? "text-green-600"
                          : op.success_rate >= 80
                            ? "text-yellow-600"
                            : "text-red-600"
                      }
                    >
                      {op.success_rate}%
                    </span>
                  </td>
                  <td className="pr-4 py-1">{op.avg_duration_ms}ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {errors && errors.length > 0 && (
        <div>
          <div className="font-medium text-h3 mb-2 text-red-600">Erreurs récentes</div>
          <div className="flex flex-col gap-1">
            {errors.map((err, i) => (
              <div key={i} className="flex gap-3 text-h4 bg-red-50 px-3 py-2 rounded">
                <span className="text-neutral-500 shrink-0">
                  {err.timestamp ? new Date(err.timestamp).toLocaleString("fr-FR") : "-"}
                </span>
                <span className="text-neutral-500 shrink-0 font-mono">{err.operation}</span>
                <span className="text-red-700">{err.error}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LogsTab({ logs }: { logs: ProxyInfo["proxy_recent_logs"] }) {
  if (!logs || logs.length === 0) {
    return <div className="text-neutral-400 text-h4 py-4">Aucun log disponible</div>;
  }

  const levelColors: Record<string, string> = {
    error: "text-red-600 bg-red-50",
    warn: "text-yellow-700 bg-yellow-50",
    info: "text-neutral-600 bg-neutral-50",
  };

  return (
    <div className="flex flex-col gap-0.5 max-h-[400px] overflow-y-auto font-mono text-h4">
      {[...logs].reverse().map((log, i) => {
        const color = levelColors[log.level] || levelColors.info;
        return (
          <div key={i} className={`flex gap-2 px-2 py-1 rounded ${color}`}>
            <span className="text-neutral-400 shrink-0 w-[150px]">
              {log.ts ? new Date(log.ts).toLocaleString("fr-FR") : ""}
            </span>
            <span className="shrink-0 w-[45px] uppercase font-semibold">{log.level}</span>
            {log.step && (
              <span className="text-neutral-400 shrink-0">[{log.step}]</span>
            )}
            <span className="break-all">{log.message}</span>
          </div>
        );
      })}
    </div>
  );
}

function OperationsRow({ op }: { op: ProxyOperation }) {
  const ordreId = op.ordre_id || "";
  const dossierId = op.dossier_id || "";
  return (
    <tr className="border-t border-neutral-200">
      <td className="pr-3 py-1.5 text-neutral-500 whitespace-nowrap">
        {op.timestamp ? new Date(op.timestamp).toLocaleString("fr-FR") : "-"}
      </td>
      <td className="pr-3 py-1.5 font-mono">{op.operation}</td>
      <td className="pr-3 py-1.5">{op.operator_email || op.username || "-"}</td>
      <td className="pr-3 py-1.5">
        {op.success ? (
          <span className="text-green-600 font-medium">OK</span>
        ) : (
          <span className="text-red-600 font-medium">Échec</span>
        )}
      </td>
      <td className="pr-3 py-1.5">{op.duration_ms}ms</td>
      <td className="pr-3 py-1.5 font-mono text-h5 text-neutral-600">
        {ordreId ? (
          <a href={`/ordre/${ordreId}`} className="text-blue-600 hover:underline" title={ordreId}>
            {ordreId.slice(0, 8)}…
          </a>
        ) : dossierId ? (
          <span title={`dossier ${dossierId}`}>{dossierId}</span>
        ) : (
          "-"
        )}
      </td>
      <td className="pr-3 py-1.5 text-red-600 max-w-[300px] truncate" title={op.error}>
        {op.error || "-"}
      </td>
    </tr>
  );
}

function OperationsTab({
  orgId,
  operations,
}: {
  orgId: string;
  operations: ProxyInfo["proxy_recent_operations"];
}) {
  const [showAll, setShowAll] = useState(false);

  return (
    <>
      <div className="flex justify-end mb-2">
        <SecondaryButton label="Voir tout (filtres + historique)" onClick={() => setShowAll(true)} variant="small" />
      </div>
      {!operations || operations.length === 0 ? (
        <div className="text-neutral-400 text-h4 py-4">Aucune opération récente</div>
      ) : (
        <div className="max-h-[400px] overflow-y-auto">
          <table className="w-full text-h4">
            <thead>
              <tr className="text-neutral-500 text-left">
                <th className="pr-3 py-1 font-medium">Date</th>
                <th className="pr-3 py-1 font-medium">Opération</th>
                <th className="pr-3 py-1 font-medium">Opérateur</th>
                <th className="pr-3 py-1 font-medium">Statut</th>
                <th className="pr-3 py-1 font-medium">Durée</th>
                <th className="pr-3 py-1 font-medium">Ordre / Dossier</th>
                <th className="pr-3 py-1 font-medium">Erreur</th>
              </tr>
            </thead>
            <tbody>
              {operations.map((op, i) => (
                <OperationsRow key={i} op={op as ProxyOperation} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showAll && <AllOperationsModal orgId={orgId} onClose={() => setShowAll(false)} />}
    </>
  );
}

function AllOperationsModal({ orgId, onClose }: { orgId: string; onClose: () => void }) {
  const PAGE_SIZE = 50;
  const [filters, setFilters] = useState<ProxyOperationsFilters>({});
  const [items, setItems] = useState<ProxyOperation[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  // Local input state (only commit to filters on Apply / Enter, to avoid spamming the API)
  const [ordreInput, setOrdreInput] = useState("");
  const [dossierInput, setDossierInput] = useState("");
  const [operationInput, setOperationInput] = useState("");
  const [successInput, setSuccessInput] = useState<"" | "ok" | "ko">("");

  const fetchPage = useCallback(
    async (reset: boolean) => {
      setLoading(true);
      setError("");
      try {
        const page = await proxyAdminApi.listOperations(orgId, {
          ...filters,
          limit: PAGE_SIZE,
          cursor_timestamp: reset ? undefined : cursor ?? undefined,
        });
        setItems((prev) => (reset ? page.items : [...prev, ...page.items]));
        setCursor(page.next_cursor);
        setHasMore(page.has_more);
      } catch (e: any) {
        setError(e?.response?.data?.detail || e?.message || "Erreur");
      } finally {
        setLoading(false);
      }
    },
    [orgId, filters, cursor],
  );

  // Refetch whenever the committed filters change (incl. initial mount when filters={})
  useEffect(() => {
    setItems([]);
    setCursor(null);
    setHasMore(false);
    fetchPage(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, filters]);

  const applyFilters = () => {
    setFilters({
      ordre_id: ordreInput.trim() || undefined,
      dossier_id: dossierInput.trim() || undefined,
      operation: operationInput.trim() || undefined,
      success: successInput === "ok" ? true : successInput === "ko" ? false : undefined,
    });
  };

  const clearFilters = () => {
    setOrdreInput("");
    setDossierInput("");
    setOperationInput("");
    setSuccessInput("");
    setFilters({});
  };

  return (
    <BaseModal title="Historique des opérations proxy" onClose={onClose} width="max-w-6xl">
      <div className="p-4 flex flex-col gap-3">
        {/* Filter row */}
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex flex-col">
            <label className="text-h5 text-neutral-500 mb-1">Ordre ID</label>
            <input
              type="text"
              className="border border-neutral-300 rounded-lg px-2 py-1 text-h4 font-mono w-48"
              value={ordreInput}
              onChange={(e) => setOrdreInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              placeholder="6PrXK9Itg0mGUvxzLP0h"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-h5 text-neutral-500 mb-1">Dossier ID (ServiceBox)</label>
            <input
              type="text"
              className="border border-neutral-300 rounded-lg px-2 py-1 text-h4 font-mono w-44"
              value={dossierInput}
              onChange={(e) => setDossierInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              placeholder="1778141902469"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-h5 text-neutral-500 mb-1">Opération</label>
            <select
              className="border border-neutral-300 rounded-lg px-2 py-1 text-h4 w-44"
              value={operationInput}
              onChange={(e) => setOperationInput(e.target.value)}
            >
              <option value="">— Toutes —</option>
              <option value="transfer_to_alpha">transfer_to_alpha</option>
              <option value="create_rdv">create_rdv</option>
              <option value="fetch_estimation">fetch_estimation</option>
              <option value="test_connection">test_connection</option>
              <option value="options">options</option>
              <option value="search_client">search_client</option>
              <option value="delete_rdv">delete_rdv</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-h5 text-neutral-500 mb-1">Statut</label>
            <select
              className="border border-neutral-300 rounded-lg px-2 py-1 text-h4 w-32"
              value={successInput}
              onChange={(e) => setSuccessInput(e.target.value as "" | "ok" | "ko")}
            >
              <option value="">— Tous —</option>
              <option value="ok">OK</option>
              <option value="ko">Échec</option>
            </select>
          </div>
          <Button label="Filtrer" onClick={applyFilters} variant="small" />
          <SecondaryButton label="Réinitialiser" onClick={clearFilters} variant="small" />
        </div>

        {error && <div className="text-red-600 text-h4">{error}</div>}

        {/* Results */}
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <div className="max-h-[60vh] overflow-y-auto">
            <table className="w-full text-h4">
              <thead className="bg-neutral-50 sticky top-0">
                <tr className="text-neutral-500 text-left">
                  <th className="pr-3 py-2 pl-3 font-medium">Date</th>
                  <th className="pr-3 py-2 font-medium">Opération</th>
                  <th className="pr-3 py-2 font-medium">Opérateur</th>
                  <th className="pr-3 py-2 font-medium">Statut</th>
                  <th className="pr-3 py-2 font-medium">Durée</th>
                  <th className="pr-3 py-2 font-medium">Ordre / Dossier</th>
                  <th className="pr-3 py-2 font-medium">Erreur</th>
                </tr>
              </thead>
              <tbody>
                {items.map((op) => (
                  <OperationsRow key={op.id || op.timestamp} op={op} />
                ))}
                {!loading && items.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-neutral-400 py-6 text-h4">
                      Aucune opération trouvée
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer: count + load more */}
        <div className="flex justify-between items-center">
          <div className="text-h5 text-neutral-500">{items.length} résultats</div>
          {hasMore && (
            <SecondaryButton
              label={loading ? "Chargement…" : "Charger 50 de plus"}
              onClick={() => fetchPage(false)}
              variant="small"
              disabled={loading}
            />
          )}
          {!hasMore && items.length > 0 && (
            <div className="text-h5 text-neutral-400">Fin de l'historique</div>
          )}
        </div>
      </div>
    </BaseModal>
  );
}
